import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import { ExternalFinancialTransaction, FinancialTransactionOption } from '../types';
import AddExternalFinancialTransactionModal from '../modals/AddExternalFinancialTransactionModal';

type DirectorBalance = {
  source: string;
  source_label: string;
  director_name: string;
  balance: number;
  last_transaction_date: string | null;
};

const DIRECTOR_SOURCES = ['ben_personal_bank', 'isaac_personal_bank'] as const;
type DirectorSource = typeof DIRECTOR_SOURCES[number];
type DirectorFilter = 'all' | DirectorSource;
const PER_PAGE = 25;

const DIRECTOR_BUTTONS: { source: DirectorSource; label: string; name: string }[] = [
  { source: 'ben_personal_bank', label: "Add to Ben's account", name: 'Ben' },
  { source: 'isaac_personal_bank', label: "Add to Isaac's account", name: 'Isaac' },
];

const DIRECTOR_FILTERS: { value: DirectorFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ben_personal_bank', label: 'Ben' },
  { value: 'isaac_personal_bank', label: 'Isaac' },
];

const isDirectorFilter = (value: string | null): value is DirectorFilter =>
  value === 'all' || DIRECTOR_SOURCES.includes(value as DirectorSource);

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(isDateOnly ? `${value}T00:00:00` : value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (amount: string): string => {
  const value = parseFloat(amount);
  if (isNaN(value)) return amount;
  const formatted = formatCurrency(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
};

const renderBalanceInterpretation = (balance: DirectorBalance) => {
  if (balance.balance < 0) {
    return (
      <p className="mt-2 text-base text-red-700">
        Tini owes {balance.director_name} <span className="font-semibold">{formatCurrency(Math.abs(balance.balance))}</span>
      </p>
    );
  }
  if (balance.balance > 0) {
    return (
      <p className="mt-2 text-base text-orange-700">
        {balance.director_name} owes Tini <span className="font-semibold">{formatCurrency(balance.balance)}</span>
      </p>
    );
  }
  return <p className="mt-2 text-base text-green-700 font-medium">Settled</p>;
};

const ExpenseClaims: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [balances, setBalances] = useState<DirectorBalance[] | null>(null);
  const [transactions, setTransactions] = useState<ExternalFinancialTransaction[]>([]);
  const [sources, setSources] = useState<FinancialTransactionOption[]>([]);
  const [categories, setCategories] = useState<FinancialTransactionOption[]>([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);

  const [directorFilter, setDirectorFilter] = useState<DirectorFilter>(() => {
    const value = searchParams.get('director');
    return isDirectorFilter(value) ? value : 'all';
  });

  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [refetchCounter, setRefetchCounter] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [lockedSource, setLockedSource] = useState<DirectorSource | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<ExternalFinancialTransaction | null>(null);
  const [duplicateSourceTransaction, setDuplicateSourceTransaction] = useState<ExternalFinancialTransaction | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setSearchParams(prev => { prev.set('page', String(page)); return prev; }, { replace: true });
    setCurrentPage(page);
  };

  const handleDirectorFilterChange = (value: DirectorFilter) => {
    if (value === directorFilter) return;
    setSearchParams(prev => {
      if (value === 'all') {
        prev.delete('director');
      } else {
        prev.set('director', value);
      }
      prev.set('page', '1');
      return prev;
    }, { replace: true });
    setDirectorFilter(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (openActionMenuId === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.action-menu')) {
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionMenuId]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [sourcesRes, categoriesRes] = await Promise.all([
          fetchWithAuth('/external_financial_transactions/sources'),
          fetchWithAuth('/external_financial_transactions/categories'),
        ]);
        if (sourcesRes.ok) setSources(await sourcesRes.json());
        if (categoriesRes.ok) setCategories(await categoriesRes.json());
      } catch (err) {
        console.error('Error loading source/category options:', err);
      }
    };
    fetchOptions();
  }, []);

  const loadBalances = useCallback(async () => {
    setIsLoadingBalances(true);
    setError(null);
    try {
      const res = await fetchWithAuth('/expense_claims');
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setBalances(data.balances);
    } catch (err) {
      console.error('Error loading expense claim balances:', err);
      setError('Failed to load expense claim balances.');
      setBalances(null);
    } finally {
      setIsLoadingBalances(false);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    setIsLoadingTransactions(true);
    setTransactionsError(null);
    try {
      const query = new URLSearchParams();
      const filteredSources = directorFilter === 'all' ? DIRECTOR_SOURCES : [directorFilter];
      filteredSources.forEach(source => query.append('source[]', source));
      query.append('page', String(currentPage));
      query.append('per_page', String(PER_PAGE));

      const res = await fetchWithAuth(`/external_financial_transactions?${query.toString()}`);
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setTransactions(data.external_financial_transactions || []);
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || 1);
        setTotalCount(data.pagination.total_count || 0);
      }
    } catch (err) {
      console.error('Error loading expense claim transactions:', err);
      setTransactionsError('Failed to load transactions.');
      setTransactions([]);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [currentPage, directorFilter]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances, refetchCounter]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions, refetchCounter]);

  const openAddModal = (source: DirectorSource) => {
    setSelectedTransaction(null);
    setDuplicateSourceTransaction(null);
    setLockedSource(source);
    setIsAddModalOpen(true);
  };

  const directorSourceOf = (txn: ExternalFinancialTransaction): DirectorSource | null =>
    DIRECTOR_SOURCES.includes(txn.finance_source as DirectorSource)
      ? (txn.finance_source as DirectorSource)
      : null;

  const openEditModal = (txn: ExternalFinancialTransaction) => {
    setSelectedTransaction(txn);
    setDuplicateSourceTransaction(null);
    setLockedSource(directorSourceOf(txn));
    setIsAddModalOpen(true);
  };

  const openDuplicateModal = (txn: ExternalFinancialTransaction) => {
    setSelectedTransaction(null);
    setDuplicateSourceTransaction(txn);
    setLockedSource(directorSourceOf(txn));
    setIsAddModalOpen(true);
  };

  const handleDelete = async (txn: ExternalFinancialTransaction) => {
    setOpenActionMenuId(null);
    if (!window.confirm('Delete this expense claim transaction? This cannot be undone.')) return;

    try {
      const res = await fetchWithAuth(`/external_financial_transactions/${txn.id}`, { method: 'DELETE' });
      if (res.status === 204) {
        toast.success('Transaction deleted');
        setRefetchCounter(c => c + 1);
        return;
      }
      if (res.status === 404) {
        toast.success('Transaction was already deleted — refreshing the list.');
        setRefetchCounter(c => c + 1);
        return;
      }
      toast.error('Failed to delete transaction');
    } catch (err) {
      console.error('Error deleting transaction:', err);
      toast.error('Failed to delete transaction');
    }
  };

  const lockedSourceLabel = lockedSource
    ? sources.find(s => s.key === lockedSource)?.label
    : undefined;
  const lockedDirectorName = lockedSource
    ? DIRECTOR_BUTTONS.find(b => b.source === lockedSource)?.name
    : undefined;
  const modalVerb = selectedTransaction ? 'Edit' : duplicateSourceTransaction ? 'Duplicate' : 'Add';
  const modalTitle = lockedDirectorName
    ? `${modalVerb} Expense Claim — ${lockedDirectorName}`
    : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Expense Claims</h1>
        <p className="text-gray-600">
          Running balance of every transaction on Ben&apos;s and Isaac&apos;s personal bank accounts. Inter-account transfers settle the balance automatically.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {DIRECTOR_BUTTONS.map(({ source, label }) => (
          <button
            key={source}
            type="button"
            onClick={() => openAddModal(source)}
            disabled={sources.length === 0}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {isLoadingBalances && !balances && (
          <>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-gray-500">Loading…</div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-gray-500">Loading…</div>
          </>
        )}
        {balances?.map(balance => (
          <div key={balance.source} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="text-xs uppercase tracking-wide text-gray-500">{balance.source_label}</div>
            <h2 className="text-xl font-semibold text-gray-900 mt-1">{balance.director_name}</h2>
            {renderBalanceInterpretation(balance)}
            <p className="mt-3 text-xs text-gray-500">
              {balance.last_transaction_date
                ? `Last transaction: ${formatDate(balance.last_transaction_date)}`
                : 'No transactions yet'}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Show:</span>
        <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Filter transactions by director">
          {DIRECTOR_FILTERS.map(({ value, label }, index) => {
            const isActive = directorFilter === value;
            const isFirst = index === 0;
            const isLast = index === DIRECTOR_FILTERS.length - 1;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isActive}
                onClick={() => handleDirectorFilterChange(value)}
                className={`px-4 py-2 text-sm font-medium border border-gray-300 -ml-px first:ml-0 ${
                  isFirst ? 'rounded-l-md' : ''
                } ${isLast ? 'rounded-r-md' : ''} ${
                  isActive
                    ? 'bg-blue-500 text-white border-blue-500 z-10'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {transactionsError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded">
          {transactionsError}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto overflow-y-visible">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoadingTransactions && transactions.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading transactions…</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No transactions yet.</td></tr>
            ) : (
              transactions.map(txn => {
                const value = parseFloat(txn.amount);
                const amountClass = !isNaN(value) && value < 0 ? 'text-red-600' : 'text-gray-900';
                const isMenuOpen = openActionMenuId === txn.id;
                return (
                  <tr key={txn.id} className={`hover:bg-gray-50 ${isMenuOpen ? 'bg-yellow-100' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(txn.date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs break-words">{txn.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{txn.category_label || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{txn.finance_source_label}</td>
                    <td className={`px-4 py-3 text-sm text-right whitespace-nowrap tabular-nums ${amountClass}`}>{formatAmount(txn.amount)}</td>
                    <td className="px-4 py-3 text-right text-sm relative overflow-visible">
                      <div className="action-menu relative inline-block text-left">
                        <button
                          type="button"
                          onClick={() => setOpenActionMenuId(isMenuOpen ? null : txn.id)}
                          className="p-1 rounded hover:bg-gray-100"
                          aria-label="Open actions menu"
                        >
                          <span className="text-xl">⋯</span>
                        </button>
                        {isMenuOpen && (
                          <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded shadow-md z-10">
                            <button
                              type="button"
                              onClick={() => {
                                openEditModal(txn);
                                setOpenActionMenuId(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                openDuplicateModal(txn);
                                setOpenActionMenuId(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Duplicate
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(txn)}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <p className="text-sm text-gray-700">
              Showing page <span className="font-medium">{currentPage}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
              {totalCount > 0 && (
                <span className="text-gray-500"> · {totalCount} transaction{totalCount === 1 ? '' : 's'}</span>
              )}
            </p>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>

      <AddExternalFinancialTransactionModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedTransaction(null);
          setDuplicateSourceTransaction(null);
          setLockedSource(null);
        }}
        onSuccess={() => {
          setRefetchCounter(c => c + 1);
        }}
        sources={sources}
        categories={categories}
        selectedTransaction={selectedTransaction}
        duplicateFromTransaction={duplicateSourceTransaction}
        lockedSource={lockedSource || undefined}
        lockedSourceLabel={lockedSourceLabel}
        titleOverride={modalTitle}
      />
    </div>
  );
};

export default ExpenseClaims;
